import { ActionTree, GetterTree, MutationTree } from 'vuex'
import { getSubjectsAlphabetical } from '@/utils/utils'
import { RootState } from '~/types/store'
import { User, UserSubject, TestAttempt } from '~/types/user'

const returnUrlbase = process.env.APP_URL

export interface AuthState {
  user: User | Record<string, never>
  userSubjects: UserSubject[]
}

export function state (): AuthState {
  return ({
  user: {},
  userSubjects: []
})
}

export const mutations: MutationTree<AuthState> = {
  SET_USER (state, data: User) {
    state.user = data
  },
  SET_USER_SUBJECTS (state, data: UserSubject[]) {
    state.userSubjects = data
  },
  UPDATE_USER_SUBJECTS_TEST_ATTEMPT (state, { subjectId, updatedTestAttempt }: { subjectId: UserSubject['id'], updatedTestAttempt: TestAttempt }) {
    const updatedSubjectId = state.userSubjects.findIndex(subject => subject.id === subjectId)

    if (updatedSubjectId !== -1) {
      state.userSubjects[updatedSubjectId].latestTestAttempt = updatedTestAttempt
    }
  }
}

export const getters: GetterTree<AuthState, RootState> = {
  isLoggedIn (state) {
    return state.user !== null && Object.keys(state.user).length !== 0
  },
  hasCompletedOnboarding (state) {
    return (state.user as User).onboardingCompleted || false
  },
  isTeacher (state) {
    return (state.user as User).educationRole === 'teacher'
  },
  subscribedToNewsletter (state, getters) {
    if (!getters.isLoggedIn) return false
    return (state.user as User).emailPreference === 'accepted'
  },
  userSubjectsHasAccess (state) {
    if (!state.userSubjects.length) return []
    return state.userSubjects.filter(subject => subject.stream && subject.stream.userHasAccess)
  },
  userSubjectsNoAccess (state) {
    if (!state.userSubjects.length) return []
    return state.userSubjects.filter(subject => (!subject.stream || !subject.stream.userHasAccess) && !subject.stream?.isLightStream)
  },
  userSubjectsByAlphabeticalThenLevel (state) {
    if (!state.userSubjects.length) return []
    // userSubjects carry name/levelName at runtime; the loose UserSubject type
    // (index-signatured) isn't statically assignable to the helper's SubjectLike.
    return getSubjectsAlphabetical(state.userSubjects as unknown as Parameters<typeof getSubjectsAlphabetical>[0])
  },
  userLightStreamSubjects (state) {
    return state.userSubjects.filter(subject => subject.stream?.isLightStream)
  },
  userFullStreamSubjects (state, getters) {
    return (getters.userSubjectsHasAccess as UserSubject[]).filter(subject => !(subject.stream && subject.stream.isLightStream))
  }
}

export const actions: ActionTree<AuthState, RootState> = {
  getUser ({ commit }) {
    return new Promise((resolve, reject) => {
      this.$axios.get('/v1/users/me')
        .then((res) => {
          if (!res.data.user) return reject(new Error(`Could not fetch user, response: ${JSON.stringify(res.data)}`))
          commit('SET_USER', res.data.user)
          return resolve(res.data.user)
        })
        .catch((e) => {
          return reject(e)
        })
    })
  },
  async getUserSubjects ({ commit }) {
    const { data } = await this.$axios.get('/v1/users/me/subjects')
    commit('SET_USER_SUBJECTS', data)
  },
  async getRedirectUrl (context, returnUrl = '/mijnexamenbundel') {
    const { data } = await this.$axios.post(`/auth/authorize?returnUrl=${returnUrlbase}${returnUrl}`)
    return data.redirect_url
  },
  async updateUser ({ commit }, fields: Partial<User>) {
    try {
      const { data } = await this.$axios.patch('/v1/users/me', fields)
      commit('SET_USER', data)
      return data
    } catch (e) {
      console.log(e)
    }
  },
  async updateUserLevels ({ commit }, levels: { levels: string[] }) {
    try {
      const { data } = await this.$axios.patch('/v1/users/me/levels', levels)
      commit('SET_USER', data)
      return data
    } catch (e) {
      console.log(e)
    }
  },
  async updateUserSubjects ({ commit, dispatch }, ids: { ids: Array<number | string> }) {
    try {
      const { data } = await this.$axios.post('/v1/users/me/subjects', ids)
      commit('SET_USER_SUBJECTS', data)
      dispatch('notifications/show', {
        type: 'success',
        text: 'Je hebt een vak toegevoegd.',
        closeButtonIconName: 'cross',
        closeTimeout: 4000
      }, { root: true })
    } catch (e) {
      console.log(e)
      dispatch('notifications/show', {
        type: 'error',
        text: 'Er is iets helaas niet goed gegaan, probeer het op een later moment.',
        closeButtonIconName: 'cross',
        closeTimeout: 4000
      }, { root: true })
    }
  },
  async deleteUserSubject ({ commit, dispatch }, id: number | string) {
    try {
      const { data } = await this.$axios.delete(`/v1/users/me/subjects/${id}`)
      commit('SET_USER_SUBJECTS', data)
      dispatch('notifications/show', {
        type: 'success',
        text: 'Je hebt een vak verwijderd.',
        closeButtonIconName: 'cross',
        closeTimeout: 4000
      }, { root: true })
    } catch (e) {
      console.log(e)
      dispatch('notifications/show', {
        type: 'error',
        text: 'Er is iets helaas niet goed gegaan, probeer het op een later moment.',
        closeButtonIconName: 'cross',
        closeTimeout: 4000
      }, { root: true })
    }
  },
  logout (context, returnUrl?: string) {
    return new Promise((resolve, reject) => {
      if (!returnUrl) return reject(new Error('Please provide a return url'))
      return resolve(`${process.env.API_URL}/auth/logout?returnUrl=${returnUrlbase}${returnUrl}`)
    })
  },
  async deleteUser () {
    await this.$axios.delete('/v1/users/me')
  },
  // autologin - not in use now
  getRedirectNoPrompt (context, returnUrl?: string) {
    return new Promise((resolve, reject) => {
      if (!returnUrl) return reject(new Error('Please provide a return url'))
      return resolve(`${process.env.API_URL}/auth/authorize-noprompt?returnUrl=${returnUrlbase}${returnUrl}`)
    })
  }
}
